import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import AIAnalystPanel from '@/components/ai/AIAnalystPanel';
import { useTheme } from '@/contexts/ThemeContext';
import { useProject } from '@/contexts/ProjectContext';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  HardHat, Search, RefreshCw, Plus, FileSpreadsheet,
  LayoutList, Settings2, ClipboardEdit, Scale, TrendingUp,
  ArrowUpRight, ArrowDownLeft, Grid3X3, List, Calendar, CalendarDays,
  Smartphone, Gauge,
} from 'lucide-react';

// Sub-components
import FCActivityRegister from '@/components/construction/FCActivityRegister';
import FCMatrixGrid from '@/components/construction/FCMatrixGrid';
import FCConfiguration from '@/components/construction/FCConfiguration';
import FCProgressEntry from '@/components/construction/FCProgressEntry';
import FCContributionMargins from '@/components/construction/FCContributionMargins';
import FCAnalytics from '@/components/construction/FCAnalytics';
import FCProductivity from '@/components/construction/FCProductivity';
import ModuleHelp from '@/components/common/ModuleHelp';
import ModuleInsightsDashboard from '@/components/common/ModuleInsightsDashboard';
import GuidedImportWizard from '@/components/shared/GuidedImportWizard';
import { FABRICATION_CONSTRUCTION_CONFIG } from '@/components/shared/importFieldConfigs';

const TABS = [
  { id: 'register', label: 'Activity Register', icon: LayoutList },
  { id: 'insights', label: 'AI Insights', icon: TrendingUp },
  { id: 'config', label: 'Configuration', icon: Settings2 },
  { id: 'progress', label: 'Progress Entry', icon: ClipboardEdit },
  { id: 'margins', label: 'Contribution Margins', icon: Scale },
  { id: 'analytics', label: 'S-Curve & Analytics', icon: TrendingUp },
  { id: 'productivity', label: 'Contractor Productivity', icon: Gauge },
];

export default function ConstructionSchedule() {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = (() => {
    const t = searchParams.get('tab');
    return TABS.some(x => x.id === t) ? t : 'register';
  })();
  const [activeTab, setActiveTab] = useState(initialTab);
  // React to tab query param changes (deep-links from other pages)
  useEffect(() => {
    const t = searchParams.get('tab');
    if (t && TABS.some(x => x.id === t) && t !== activeTab) {
      setActiveTab(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const [viewMode, setViewMode] = useState('register');
  const [matrixGranularity, setMatrixGranularity] = useState('daily');
  const { selectedProject, projectId: ctxProjectId } = useProject();
  const activeProjectId = selectedProject?.project_id;
  const [activities, setActivities] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [showImportWizard, setShowImportWizard] = useState(false);

  const fetchMatrixData = useCallback(async () => {
    try {
      const [actsRes, periodsRes] = await Promise.all([
        api.get(`/v1/projects/${activeProjectId}/fc/activities?limit=500`),
        api.get(`/v1/projects/${activeProjectId}/fc/reporting-periods`)
      ]);
      setActivities(actsRes.data.activities || []);
      setPeriods(periodsRes.data?.periods || []);
    } catch { /* ignore */ }
  }, [activeProjectId]);

  useEffect(() => {
    if (activeTab === 'register' && viewMode === 'matrix') {
      fetchMatrixData();
    }
  }, [activeTab, viewMode, fetchMatrixData]);

  return (
    <DashboardLayout>
      {ctxProjectId && <AIAnalystPanel projectId={ctxProjectId} moduleKey="FABRICATION_CONSTRUCTION" defaultOpen={true} compact={true} />}

      <div className="space-y-4 p-4 md:p-6" data-testid="construction-schedule-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg" style={{ background: '#F59E0B20', border: '1px solid #F59E0B40' }}>
              <HardHat className="w-5 h-5" style={{ color: '#F59E0B' }} />
            </div>
            <div>
              <h1 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>Fabrication & Construction Schedule</h1>
              <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                Activity-driven quantity-based progress tracking with distribution curves
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/project-controls/construction/dpr')}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all hover:scale-[1.02] active:scale-95"
              style={{ background: '#3b82f6', color: '#fff' }}
              data-testid="open-daily-dpr-btn"
              title="Open mobile-friendly Daily DPR Capture for site supervisors"
            >
              <Smartphone className="w-3.5 h-3.5" /> Daily DPR
            </button>
            <button
              onClick={() => setShowImportWizard(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all hover:scale-[1.02] active:scale-95"
              style={{ background: 'var(--card-bg-alt)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
              data-testid="fc-import-btn"
              title="Guided Excel Import Wizard — template, upload, map, validate, confirm"
            >
              <FileSpreadsheet className="w-3.5 h-3.5" /> Import
            </button>
            <ModuleHelp moduleKey="construction" />
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-[10px]" style={{ background: '#3b82f610', border: '1px solid #3b82f630', color: '#3b82f6' }}>
              <ArrowDownLeft className="w-3 h-3" /> Roll-down
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-[10px]" style={{ background: '#10b98110', border: '1px solid #10b98130', color: '#10b981' }}>
              <ArrowUpRight className="w-3 h-3" /> Roll-up
            </div>
            <span className="text-xs px-3 py-1.5 rounded-lg" style={{ background: 'var(--card-bg-alt)', color: 'var(--text-muted)', border: '1px solid var(--border-color)' }}
              data-testid="fc-project-label">Project: {selectedProject?.name || 'Select a project'}</span>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 overflow-x-auto pb-1 border-b" style={{ borderColor: 'var(--border-color)' }}>
          {TABS.map(tab => {
            const Icon = tab.icon;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-t-lg whitespace-nowrap transition-colors"
                style={{
                  color: activeTab === tab.id ? 'var(--accent-primary)' : 'var(--text-muted)',
                  background: activeTab === tab.id ? 'var(--card-bg)' : 'transparent',
                  borderBottom: activeTab === tab.id ? '2px solid var(--accent-primary)' : '2px solid transparent'
                }}
                data-testid={`fc-tab-${tab.id}`}>
                <Icon className="w-3.5 h-3.5" /> {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        {activeTab === 'register' && (
          <div className="space-y-3">
            {/* View Mode Toggle */}
            <div className="flex items-center justify-between">
              <div className="flex rounded-lg overflow-hidden" style={{ border: '1px solid var(--border-color)' }} data-testid="fc-view-mode-toggle">
                <button onClick={() => setViewMode('register')} data-testid="fc-view-register-btn"
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors"
                  style={{
                    backgroundColor: viewMode === 'register' ? 'var(--accent-primary)' : 'var(--card-bg)',
                    color: viewMode === 'register' ? '#ffffff' : 'var(--text-muted)',
                  }}>
                  <List className="w-3.5 h-3.5" /> Register
                </button>
                <button onClick={() => setViewMode('matrix')} data-testid="fc-view-matrix-btn"
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors"
                  style={{
                    backgroundColor: viewMode === 'matrix' ? 'var(--accent-primary)' : 'var(--card-bg)',
                    color: viewMode === 'matrix' ? '#ffffff' : 'var(--text-muted)',
                    borderLeft: '1px solid var(--border-color)',
                  }}>
                  <Grid3X3 className="w-3.5 h-3.5" /> Matrix
                </button>
              </div>
              {viewMode === 'matrix' && (
                <div className="flex items-center gap-2">
                  <div className="flex rounded-lg overflow-hidden" style={{ border: '1px solid var(--border-color)' }} data-testid="fc-granularity-toggle">
                    <button onClick={() => setMatrixGranularity('daily')} data-testid="fc-granularity-daily-btn"
                      className="flex items-center gap-1 px-2.5 py-1 text-[10px] font-medium transition-colors"
                      style={{
                        backgroundColor: matrixGranularity === 'daily' ? 'var(--accent-primary)' : 'var(--card-bg)',
                        color: matrixGranularity === 'daily' ? '#ffffff' : 'var(--text-muted)',
                      }}>
                      <CalendarDays className="w-3 h-3" /> Daily
                    </button>
                    <button onClick={() => setMatrixGranularity('monthly')} data-testid="fc-granularity-monthly-btn"
                      className="flex items-center gap-1 px-2.5 py-1 text-[10px] font-medium transition-colors"
                      style={{
                        backgroundColor: matrixGranularity === 'monthly' ? 'var(--accent-primary)' : 'var(--card-bg)',
                        color: matrixGranularity === 'monthly' ? '#ffffff' : 'var(--text-muted)',
                        borderLeft: '1px solid var(--border-color)',
                      }}>
                      <Calendar className="w-3 h-3" /> Monthly
                    </button>
                  </div>
                  <Button size="sm" variant="ghost" onClick={fetchMatrixData} data-testid="refresh-matrix-btn">
                    <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
                  </Button>
                </div>
              )}
            </div>

            {viewMode === 'register' ? (
              <FCActivityRegister projectId={activeProjectId} />
            ) : (
              <FCMatrixGrid activities={activities} periods={periods} projectId={activeProjectId} onRefresh={fetchMatrixData} isDark={isDark} granularity={matrixGranularity} />
            )}
          </div>
        )}
        {activeTab === 'config' && <FCConfiguration projectId={activeProjectId} />}
        {activeTab === 'insights' && <ModuleInsightsDashboard projectId={activeProjectId} moduleKey="construction" color="#0EA5E9" deepLinkBase="/project-controls/construction" />}
        {activeTab === 'progress' && <FCProgressEntry projectId={activeProjectId} />}
        {activeTab === 'margins' && <FCContributionMargins projectId={activeProjectId} />}
        {activeTab === 'analytics' && <FCAnalytics projectId={activeProjectId} />}
        {activeTab === 'productivity' && <FCProductivity projectId={activeProjectId} />}
        <GuidedImportWizard
          open={showImportWizard}
          onOpenChange={setShowImportWizard}
          projectId={activeProjectId}
          onImportComplete={fetchMatrixData}
          {...FABRICATION_CONSTRUCTION_CONFIG(activeProjectId)}
        />
      </div>
    </DashboardLayout>
  );
}
